import { formatHospitalDateTime as __formatHospitalDateTime, formatHospitalTime as __formatHospitalTime } from '@/utils/timeFormat';
import { useEffect, useState, useRef } from "react";
import alarm, { toneForCode } from '@/services/emergencyAlarm';
import { enableEmergencyPush, disableEmergencyPush } from '@/services/emergencyPush';
import { AlertTriangle, X, UserCheck, CheckCircle2, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import emergencyCodeService, {
  EmergencyCode,
  EmergencyCodeActivation,
} from "@/services/emergencyCodeService";
import { useRealtimeSocket } from "@/hooks/useRealtimeSocket";

import { useNotification } from "@/contexts/NotificationContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Persistent, always-reachable emergency-code trigger + live active-codes
 * strip. Rendered once in ErpShell (not per-page) so it stays visible no
 * matter what the user is looking at when a code needs to be called.
 */
export default function EmergencyCodeBar() {
  const { user, token, loading: authLoading } = useAuth() as any;
  const authenticated = !authLoading && !!user && !!token;
  const { showWarning, showInfo } = useNotification();
  const [codes, setCodes] = useState<EmergencyCode[]>([]);
  const [active, setActive] = useState<EmergencyCodeActivation[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCodeId, setSelectedCodeId] = useState<string>("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<EmergencyCodeActivation | null>(null);
  const [silenced, setSilenced] = useState<Set<number>>(new Set());
  const [audioReady, setAudioReady] = useState(false);
  const seen = useRef(new Set<number>());
  const received = useRef(new Set<number>());
  const [events, setEvents] = useState<any[]>([]);
  const [auditError, setAuditError] = useState('');
  useEffect(() => {
    for (const incident of active) {
      if (received.current.has(incident.id)) continue;
      received.current.add(incident.id);
      void emergencyCodeService.receipt(incident.id, 'received').catch(() => received.current.delete(incident.id));
    }
  }, [active]);
  useEffect(() => {
    let cancelled = false;
    setEvents([]); setAuditError('');
    if (!selectedDetail) return;
    const refresh = () => emergencyCodeService.events(selectedDetail.id).then(data => {
      if (!cancelled) { setEvents(Array.isArray(data) ? data : (data as any)?.data || []); setAuditError(''); }
    }).catch(() => { if (!cancelled) setAuditError('Response history unavailable'); });
    void refresh();
    const timer = setInterval(refresh, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [selectedDetail?.id]);

  useEffect(() => {
    const pending = active.filter(a => a.status === 'active' && !a.attended_at && !a.attended_by_name && !silenced.has(a.id));
    const next = pending.find(a => toneForCode(a.code?.name || '') === 'fire_engine_siren') || pending[0];
    if (next) alarm.startAlarm(toneForCode(next.code?.name || ''));
    else alarm.stopAlarm();
  }, [active, silenced]);
  useEffect(() => () => alarm.stopAlarm(), []);

  const enableSound = async () => {
    try { await alarm.enable(); setAudioReady(true); await alarm.playAlertBeep('fire_engine_siren'); }
    catch { showWarning('Sound could not be enabled. Check browser audio permissions.', 8000); }
  };

  useEffect(() => {
    if (!authenticated) { setCodes([]); setActive([]); return; }
    let cancelled = false;
    emergencyCodeService.listCodes({ is_active: true }).then(data => { if (!cancelled) setCodes(data); }).catch(() => {});
    emergencyCodeService.listActive().then((data) => {
      const list = Array.isArray(data) ? data : (data as any)?.data || [];
      if (!cancelled) setActive(list);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [authenticated]);

  useEffect(() => {
    const handleOpen = () => setModalOpen(true);
    window.addEventListener('openEmergencyCodeModal', handleOpen);
    return () => window.removeEventListener('openEmergencyCodeModal', handleOpen);
  }, []);

  useRealtimeSocket({
    "connect": () => {
      if (!authenticated) return;
      emergencyCodeService.listActive().then(data => {
        setActive(Array.isArray(data) ? data : (data as any)?.data || []);
      }).catch(() => showWarning('Could not recover active alerts. Check your connection.', 8000));
    },
    "emergency-code:escalated": (item: EmergencyCodeActivation) => {
      setSilenced(prev => { const next = new Set(prev); next.delete(item.id); return next; });
      setActive(prev => [item, ...prev.filter(a => a.id !== item.id)]);
      showWarning(`No response yet: ${item.code?.name} at ${item.location}`, 10000);
    },
    "emergency-code:activated": (payload: any) => {
      console.log("[EmergencyCodeBar] Received emergency-code:activated:", payload);
      const item: EmergencyCodeActivation = payload?.data || payload;
      if (!item || !item.id) return;

      if (seen.current.has(item.id)) return;
      seen.current.add(item.id);
      const matchedCode = item.code || codes.find((c) => c.id === item.code_id);
      const codeName = matchedCode?.name || "Emergency Code";

      showWarning(`🚨 LIVE ALERT: ${codeName} broadcasted at ${item.location}!`, 9000);

      setActive((prev) => {
        const existing = prev.filter((a) => a.id !== item.id);
        const hydrated = { ...item, code: matchedCode || item.code };
        return [hydrated, ...existing];
      });
    },
    "staff-page:received": (page: any) => {
      showInfo(`Staff page: ${page.title}`, 8000);
      window.dispatchEvent(new CustomEvent("daafya:staff-page-received", { detail: page }));
    },
    "emergency-code:attended": (payload: any) => {
      console.log("[EmergencyCodeBar] Received emergency-code:attended:", payload);
      const item = payload?.data || payload;
      if (!item || !item.id) return;

      const responder = item.attended_by_name || "Staff Responder";
      showInfo(`🟢 Attended by ${responder} (En Route)`, 7000);

      setActive((prev) =>
        prev.map((a) =>
          a.id === item.id
            ? {
                ...a,
                attended_by_name: item.attended_by_name,
                attended_by_role: item.attended_by_role,
                attended_at: item.attended_at,
              }
            : a
        )
      );

      setSelectedDetail((prev) =>
        prev && prev.id === item.id
          ? {
              ...prev,
              attended_by_name: item.attended_by_name,
              attended_by_role: item.attended_by_role,
              attended_at: item.attended_at,
            }
          : prev
      );
    },
    "emergency-code:resolved": (payload: any) => {
      console.log("[EmergencyCodeBar] Received emergency-code:resolved:", payload);
      const item: EmergencyCodeActivation = payload?.data || payload;
      const targetId = item?.id;
      if (targetId) {
        showInfo(`Emergency code #${targetId} marked as resolved.`, 4000);
        setActive((prev) => prev.filter((a) => a.id !== targetId));
        setSelectedDetail((prev) => (prev?.id === targetId ? null : prev));
      }
    },
  });

  const handleActivate = async () => {
    if (!selectedCodeId || !location.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await emergencyCodeService.activate({
        code_id: Number(selectedCodeId),
        location: location.trim(),
        notes: notes.trim() || undefined,
      });
      setModalOpen(false);
      setSelectedCodeId("");
      setLocation("");
      setNotes("");
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to activate code. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAttend = async (id: number) => {
    try {
      const res = await emergencyCodeService.attend(id);
      const data = (res as any)?.data || res;
      showInfo("You have been recorded as attending (En Route).", 4000);
      setActive((prev) =>
        prev.map((a) =>
          a.id === id
            ? {
                ...a,
                attended_by_name: data?.attended_by_name || "Staff Responder",
                attended_by_role: data?.attended_by_role || "Staff",
                attended_at: data?.attended_at || new Date().toISOString(),
              }
            : a
        )
      );
      setSelectedDetail((prev) =>
        prev && prev.id === id
          ? {
              ...prev,
              attended_by_name: data?.attended_by_name || "Staff Responder",
              attended_by_role: data?.attended_by_role || "Staff",
              attended_at: data?.attended_at || new Date().toISOString(),
            }
          : prev
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to record attendance.");
    }
  };

  const handleResolve = async (id: number) => {
    try {
      await emergencyCodeService.resolve(id, { status: "resolved" });
      setActive((prev) => prev.filter((a) => a.id !== id));
      if (selectedDetail?.id === id) {
        setSelectedDetail(null);
      }
    } catch {
      // Resolve failures surface via the toast above on next active fetch.
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2 text-sm" aria-live="polite">
        <span>{audioReady ? 'Alert sound enabled' : 'Enable sound on this device to hear emergency alerts'}</span>
        <Button size="sm" variant="outline" onClick={enableSound}>Enable / test sound</Button>
        <Button size="sm" variant="outline" onClick={async () => {
          try { await enableEmergencyPush(); showInfo('Background notifications enabled on this device', 5000); }
          catch (error: any) { showWarning(error.message, 8000); }
        }}>Enable background alerts</Button>
        <Button size="sm" variant="ghost" onClick={() => void disableEmergencyPush()}>Disable background alerts</Button>
        {active.length > 0 && <Button size="sm" variant="outline" onClick={() => {
          setSilenced(new Set(active.map(a => a.id)));
          for (const a of active) void emergencyCodeService.receipt(a.id, 'silenced').catch(console.warn);
        }}>Silence on this device</Button>}
      </div>
      {active.length > 0 && (
        <div className="flex flex-col gap-1 border-b border-border bg-red-50 px-4 py-2 dark:bg-red-950/30">
          {active.map((activation) => (
            <div
              key={activation.id}
              className="flex items-center justify-between gap-3 cursor-pointer hover:bg-red-100/50 dark:hover:bg-red-900/20 p-1.5 rounded transition-colors"
              onClick={() => setSelectedDetail(activation)}
            >
              <div className="flex items-center gap-3 text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0 animate-pulse" style={{ color: activation.code?.color_hex }} />
                <span className="font-semibold" style={{ color: activation.code?.color_hex }}>
                  {activation.code?.name}
                </span>
                <span className="font-medium text-foreground bg-background/70 px-2 py-0.5 rounded border text-xs">
                  {activation.location}
                </span>
                {activation.attended_by_name ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-500/30">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Attended by {activation.attended_by_name} ({activation.attended_by_role || 'En Route'})
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300 border border-red-500/30 animate-pulse">
                    Awaiting Responder
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {!activation.attended_by_name && (
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs px-2.5 shadow-sm"
                    onClick={() => handleAttend(activation.id)}
                  >
                    <UserCheck className="mr-1 h-3.5 w-3.5" />
                    I'm Attending
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs px-2.5 text-muted-foreground hover:text-foreground"
                  onClick={() => setSelectedDetail(activation)}
                >
                  Details
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-100 dark:border-red-900/50"
                  onClick={() => handleResolve(activation.id)}
                >
                  <X className="mr-1 h-3.5 w-3.5" />
                  Clear
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}


      {/* Activate Code Dialog */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Activate Emergency Code</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Code</label>
              <Select value={selectedCodeId} onValueChange={setSelectedCodeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a code" />
                </SelectTrigger>
                <SelectContent>
                  {codes.map((code) => (
                    <SelectItem key={code.id} value={String(code.id)}>
                      <span className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: code.color_hex }}
                        />
                        {code.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label htmlFor="emergency-code-location" className="mb-1 block text-sm font-medium">Location <span className="text-destructive" aria-hidden="true">*</span></label>
              <Input
                id="emergency-code-location"
                required
                maxLength={255}
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. 3rd Floor, Room 302"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Notes (optional)</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional context for responders"
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!selectedCodeId || !location.trim() || submitting}
              onClick={handleActivate}
            >
              Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Emergency Incident Details & Attendance Screen/Modal */}
      <Dialog open={!!selectedDetail} onOpenChange={(open) => { if (!open) setSelectedDetail(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-bold">
              <AlertTriangle className="h-5 w-5 shrink-0" style={{ color: selectedDetail?.code?.color_hex }} />
              <span style={{ color: selectedDetail?.code?.color_hex }}>
                {selectedDetail?.code?.name || "Emergency Incident"}
              </span>
              <span className="ml-auto text-xs font-normal text-muted-foreground">
                ID #{selectedDetail?.id}
              </span>
            </DialogTitle>
          </DialogHeader>

          {selectedDetail && (
            <div className="flex flex-col gap-4 py-2">
              {/* Status Banner */}
              <div
                className={`p-3 rounded-lg border text-sm flex items-center justify-between ${
                  selectedDetail.attended_by_name
                    ? "bg-emerald-50 border-emerald-300 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-200"
                    : "bg-red-50 border-red-300 text-red-900 dark:bg-red-950/40 dark:border-red-800 dark:text-red-200"
                }`}
              >
                <div className="flex items-center gap-2">
                  {selectedDetail.attended_by_name ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 animate-pulse" />
                  )}
                  <div>
                    <div className="font-semibold">
                      {selectedDetail.attended_by_name
                        ? "ATTENDED • EN ROUTE"
                        : "AWAITING RESPONDERS"}
                    </div>
                    <div className="text-xs opacity-90">
                      {selectedDetail.attended_by_name
                        ? `Primary responder: ${selectedDetail.attended_by_name} (${selectedDetail.attended_by_role || "Staff"})`
                        : "Broadcast active across all nursing & doctor pagers"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Location Card */}
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg border">
                <MapPin className="h-5 w-5 text-sky-500 mt-0.5 shrink-0" />
                <div>
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Response Location
                  </div>
                  <div className="text-base font-bold text-foreground">
                    {selectedDetail.location}
                  </div>
                </div>
              </div>

              {/* Notes & Timestamp */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-muted/30 rounded-lg border">
                  <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                    <Clock className="h-3.5 w-3.5" /> Broadcasted
                  </div>
                  <div className="font-medium">
                    {__formatHospitalTime(selectedDetail.triggered_at)}
                  </div>
                </div>
                <div className="p-3 bg-muted/30 rounded-lg border">
                  <div className="text-xs text-muted-foreground mb-1">Status</div>
                  <div className="font-medium capitalize">{selectedDetail.status}</div>
                </div>
              </div>

              {selectedDetail.notes && (
                <div className="p-3 bg-muted/30 rounded-lg border text-sm">
                  <div className="text-xs text-muted-foreground mb-1">Dispatch Notes</div>
                  <p className="text-foreground">{selectedDetail.notes}</p>
                </div>
              )}

              <section aria-label="Response history" className="rounded border p-3 text-sm">
                <h3 className="font-semibold">Response history</h3>
                <p className="text-muted-foreground">{auditError || (!events.length ? 'No device receipts recorded yet' : '')}</p>
                {events.map(event => <div key={event.id} className="flex flex-wrap justify-between gap-2 border-b py-1">
                  <span>{event.event.replace(/_/g, ' ')} · {event.user_id ? `Staff #${event.user_id}` : 'System'}{event.device_id ? ` · ${event.device_id}` : ''}</span>
                  <time>{__formatHospitalDateTime(event.created_at)}</time>
                </div>)}
              </section>

              {/* Actions Footer */}
              <div className="flex items-center justify-between gap-3 pt-2 border-t">
                {!selectedDetail.attended_by_name ? (
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                    onClick={() => handleAttend(selectedDetail.id)}
                  >
                    <UserCheck className="mr-2 h-4 w-4" />
                    I'm Attending (En Route)
                  </Button>
                ) : (
                  <div className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">
                    🟢 Response underway
                  </div>
                )}
                <Button
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-950/30"
                  onClick={() => handleResolve(selectedDetail.id)}
                >
                  <X className="mr-1.5 h-4 w-4" />
                  Mark Resolved / Clear
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
